import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, ArrowRightLeft, Star, AlertTriangle } from 'lucide-react';
import { message } from 'antd';
import { V, cardStyle, cardHoverStyle, h2Style, labelStyle, overlayStyle, modalStyle } from '../vividGlass';
import brandDnaActions from '../../../../actions/brandDna';

function ConfirmModal({ title, description, danger, confirmLabel, onConfirm, onCancel, children }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={overlayStyle} onClick={onCancel}>
      <motion.div initial={{ scale: 0.94, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20 }}
        onClick={(e) => e.stopPropagation()} style={{ ...modalStyle, maxWidth: 420 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: danger ? 'rgba(245,87,108,0.12)' : 'rgba(255,210,34,0.12)', border: `1px solid ${danger ? 'rgba(245,87,108,0.3)' : 'rgba(255,210,34,0.3)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <AlertTriangle size={16} color={danger ? '#f5576c' : V.warning} />
          </div>
          <h2 style={{ ...h2Style }}>{title}</h2>
        </div>
        <p style={{ color: V.textSub, fontSize: 14, lineHeight: 1.6, marginBottom: 16, fontFamily: V.font }}>{description}</p>
        {children}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <motion.button whileTap={{ scale: 0.96 }} onClick={onCancel}
            style={{ padding: '10px 20px', borderRadius: 12, background: V.glass, border: `1px solid ${V.glassBorder}`, color: V.textSub, fontSize: 14, cursor: 'pointer', fontFamily: V.font }}>
            Cancel
          </motion.button>
          <motion.button whileTap={{ scale: 0.96 }} onClick={onConfirm}
            style={{ padding: '10px 20px', borderRadius: 12, background: danger ? 'rgba(245,87,108,0.2)' : 'rgba(255,210,34,0.15)', border: `1px solid ${danger ? 'rgba(245,87,108,0.4)' : 'rgba(255,210,34,0.4)'}`, color: danger ? '#f5576c' : V.warning, fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: V.font }}>
            {confirmLabel}
          </motion.button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function BrandSettingsCard({ brand, brandId, isOwner, onDeleted, onRefresh }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [transferEmail, setTransferEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleDelete = async () => {
    if (deleteConfirmName.trim().toLowerCase() !== brand.brand_name.trim().toLowerCase()) {
      message.warning('Brand name does not match');
      return;
    }
    setLoading(true);
    try {
      await brandDnaActions.deleteBrand(brandId);
      message.success('Brand deleted');
      onDeleted();
    } catch {
      message.error('Failed to delete brand');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleSetDefault = async () => {
    try {
      await brandDnaActions.setDefaultBrand(brandId);
      message.success('Set as default brand');
      onRefresh();
    } catch {
      message.error('Failed to set as default');
    }
  };

  const handleTransfer = async () => {
    if (!transferEmail.trim()) {
      message.warning('Please enter an email address');
      return;
    }
    setLoading(true);
    try {
      const res = await brandDnaActions.transferBrand(brandId, transferEmail.trim());
      message.success(`Ownership transferred to ${res.data?.new_owner || transferEmail}`);
      setShowTransferModal(false);
      setTransferEmail('');
      onDeleted();
    } catch (err) {
      message.error(err?.response?.data?.error || 'Failed to transfer ownership');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 10,
    background: 'rgba(255,255,255,0.06)', border: `1px solid ${V.glassBorder}`,
    color: V.text, fontSize: 14, outline: 'none', fontFamily: V.font, marginBottom: 12,
  };

  const settingRow = (icon, label, description, action, danger) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0', borderBottom: `1px solid rgba(255,255,255,0.05)` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: danger ? 'rgba(245,87,108,0.08)' : V.glass, border: `1px solid ${V.glassBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {React.cloneElement(icon, { size: 15, color: danger ? '#f5576c' : V.textMuted })}
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: danger ? '#f5576c' : V.text, fontFamily: V.font }}>{label}</div>
          <div style={{ fontSize: 12, color: V.textMuted, fontFamily: V.font }}>{description}</div>
        </div>
      </div>
      {action}
    </div>
  );

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.15 }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{ ...cardStyle, ...(hovered ? cardHoverStyle : {}), marginBottom: 40 }}
      >
        <h2 style={{ ...h2Style, marginBottom: 4 }}>Brand Settings</h2>
        <p style={{ fontSize: 14, color: V.textSub, margin: '0 0 16px', fontFamily: V.font }}>Manage ownership and lifecycle of this brand</p>

        {isOwner && !brand.is_default && settingRow(
          <Star />, 'Set as Default', 'Use this brand by default in the AI agent',
          <motion.button whileTap={{ scale: 0.96 }} onClick={handleSetDefault}
            style={{ padding: '8px 16px', borderRadius: 12, background: 'rgba(255,210,34,0.1)', border: '1px solid rgba(255,210,34,0.3)', color: V.warning, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: V.font }}>
            Set Default
          </motion.button>
        )}

        {isOwner && settingRow(
          <ArrowRightLeft />, 'Transfer Ownership', 'Hand this brand to another user',
          <motion.button whileTap={{ scale: 0.96 }} onClick={() => setShowTransferModal(true)}
            style={{ padding: '8px 16px', borderRadius: 12, background: 'rgba(79,58,125,0.5)', border: '1px solid #4F3A7D', color: '#C4B5FD', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: V.font }}>
            Transfer
          </motion.button>
        )}

        {isOwner && settingRow(
          <Trash2 />, 'Delete Brand', 'Permanently remove this brand and all its data',
          <motion.button whileTap={{ scale: 0.96 }} onClick={() => setShowDeleteModal(true)}
            style={{ padding: '8px 16px', borderRadius: 12, background: 'rgba(245,87,108,0.1)', border: '1px solid rgba(245,87,108,0.3)', color: '#f5576c', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: V.font }}>
            Delete
          </motion.button>,
          true
        )}
      </motion.div>

      <AnimatePresence>
        {showDeleteModal && (
          <ConfirmModal
            title="Delete Brand"
            danger
            description={`This action cannot be undone. All DNA data, assets, and generations for "${brand.brand_name}" will be permanently removed.`}
            confirmLabel={loading ? 'Deleting…' : 'Delete Brand'}
            onConfirm={handleDelete}
            onCancel={() => { setShowDeleteModal(false); setDeleteConfirmName(''); }}
          >
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...labelStyle }}>
                Type <strong style={{ color: V.text, fontWeight: 900 }}>{brand.brand_name}</strong> to confirm
              </label>
              <input
                value={deleteConfirmName}
                onChange={(e) => setDeleteConfirmName(e.target.value)}
                placeholder={brand.brand_name}
                style={inputStyle}
              />
            </div>
          </ConfirmModal>
        )}

        {showTransferModal && (
          <ConfirmModal
            title="Transfer Ownership"
            description="The new owner will gain full control. You will become a collaborator on this brand."
            confirmLabel={loading ? 'Transferring…' : 'Transfer'}
            onConfirm={handleTransfer}
            onCancel={() => { setShowTransferModal(false); setTransferEmail(''); }}
          >
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...labelStyle }}>New owner's email address</label>
              <input
                value={transferEmail}
                onChange={(e) => setTransferEmail(e.target.value)}
                placeholder="owner@example.com"
                type="email"
                style={inputStyle}
              />
            </div>
          </ConfirmModal>
        )}
      </AnimatePresence>
    </>
  );
}
